import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
// Mock data store for deep dives
const postData: Record<string, any> = {
  'biomimicry-market-landscape': {
    title: 'The Biomimicry Market Landscape',
    tag: 'Data Viz',
    color: '#78e1c4',
    date: 'March 2026',
    readTime: '8 min',
    content: `
      <p class="text-xl text-gray-300 leading-relaxed mb-8">
        Nature has been running the ultimate R&D lab for 3.8 billion years. Today, we are seeing a Cambrian explosion of startups translating biological intelligence into commercial solutions.
      </p>
      <h2 class="text-2xl font-bold text-white mt-12 mb-6">The Growth of Bio-Inspired Tech</h2>
      <p class="text-gray-300 leading-relaxed mb-6">
        Over the last decade, investment in biomimicry-driven startups has accelerated. We're moving beyond simple morphological mimicry (like Velcro) into deep systemic mimicry—understanding how nature builds materials at ambient temperatures, how it processes information with minimal energy, and how it creates circular economies where waste equals food.
      </p>
      
      <div class="my-12 p-8 rounded-2xl border-2 border-[#78e1c4]/30 bg-[#1c3f4d]/40 flex flex-col items-center justify-center min-h-[400px]">
        <div class="w-16 h-16 rounded-full bg-[#78e1c4]/20 flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#78e1c4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
        </div>
        <h3 class="text-xl font-bold text-white mb-2">Interactive Data Visualization</h3>
        <p class="text-gray-400 text-center max-w-md">
          This space is reserved for an interactive D3.js or Recharts visualization showing funding growth across Grow, Build, and Use sectors.
        </p>
      </div>

      <h2 class="text-2xl font-bold text-white mt-12 mb-6">Key Sectors Ripe for Disruption</h2>
      <p class="text-gray-300 leading-relaxed mb-6">
        Our analysis indicates that agriculture (Grow), the built environment (Build), and consumer materials (Use) represent the most immediate opportunities for nature-inspired solutions to achieve venture-scale returns while driving systemic resilience.
      </p>
    `
  },
  'soil-carbon-economics': {
    title: 'The Economics of Soil Carbon',
    tag: 'Analysis',
    color: '#edcd99',
    date: 'February 2026',
    readTime: '12 min',
    content: `
      <p class="text-xl text-gray-300 leading-relaxed mb-8">
        Healthy soil is the foundation of terrestrial life. It's also one of the most underutilized economic assets in the fight against climate change and systemic fragility.
      </p>
      <h2 class="text-2xl font-bold text-white mt-12 mb-6">Beyond Yield: The Compounding Value of Soil</h2>
      <p class="text-gray-300 leading-relaxed mb-6">
        For decades, agricultural economics has focused almost exclusively on yield per acre. This linear model ignores the compounding value of soil organic matter. When we shift to regenerative practices that mimic natural prairie ecosystems, we unlock multiple value streams simultaneously.
      </p>
      
      <div class="my-12 p-8 rounded-2xl border-2 border-[#edcd99]/30 bg-[#1c3f4d]/40 flex flex-col items-center justify-center min-h-[400px]">
        <div class="w-16 h-16 rounded-full bg-[#edcd99]/20 flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#edcd99" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path><path d="M22 12A10 10 0 0 0 12 2v10z"></path></svg>
        </div>
        <h3 class="text-xl font-bold text-white mb-2">Value Stream Analysis</h3>
        <p class="text-gray-400 text-center max-w-md">
          This space is reserved for a data visualization breaking down the economic benefits of a 1% increase in soil organic matter (water retention, input reduction, carbon credits, yield stability).
        </p>
      </div>

      <h2 class="text-2xl font-bold text-white mt-12 mb-6">The Role of Deep Tech</h2>
      <p class="text-gray-300 leading-relaxed mb-6">
        The bottleneck to scaling regenerative agriculture hasn't been a lack of desire, but a lack of measurement and management tools. Startups leveraging AI, satellite imagery, and autonomous robotics are finally making it possible to manage biological complexity at scale.
      </p>
    `
  }
};
export function DeepDiveReader() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = slug ? postData[slug] : null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!post) {
    return <div className="min-h-screen pt-32 pb-20 bg-deepCanopy flex flex-col items-center justify-center" data-id="element-533">
        <h1 className="text-3xl font-bold text-white mb-4" data-id="element-534">Post not found</h1>
        <Link to="/insights" className="text-vitalityTeal hover:underline flex items-center" data-id="element-535">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-536" /> Back to Insights
        </Link>
      </div>;
  }
  return <div className="min-h-screen pt-32 pb-20 bg-deepCanopy" data-id="element-537">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-538">
        {/* Back Link */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.5
      }} className="mb-12" data-id="element-539">
          <Link to="/insights" className="inline-flex items-center text-gray-400 hover:text-white transition-colors" data-id="element-540">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-541" />
            Back to Insights
          </Link>
        </motion.div>

        {/* Header */}
        <motion.header initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="mb-16" data-id="element-542">
          <div className="flex items-center gap-4 mb-6" data-id="element-543">
            <span className="px-3 py-1 rounded-full text-xs font-medium border uppercase tracking-wider" style={{
            color: post.color,
            borderColor: `${post.color}50`,
            backgroundColor: `${post.color}10`
          }} data-id="element-544">
              {post.tag}
            </span>
            <div className="flex items-center text-gray-400 text-sm" data-id="element-545">
              <Calendar className="w-4 h-4 mr-1.5" data-id="element-546" />
              {post.date}
            </div>
            <div className="flex items-center text-gray-400 text-sm" data-id="element-547">
              <Clock className="w-4 h-4 mr-1.5" data-id="element-548" />
              {post.readTime}
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8" data-id="element-549">
            {post.title}
          </h1>

          <div className="h-px w-full bg-gradient-to-r from-white/20 to-transparent" data-id="element-550" />
        </motion.header>

        {/* Content */}
        <motion.article initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="prose prose-lg prose-invert max-w-none" dangerouslySetInnerHTML={{
        __html: post.content
      }} data-id="element-551" />

        {/* Footer */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-20 pt-8 border-t border-white/10 flex justify-between items-center" data-id="element-552">
          <div className="flex items-center space-x-4" data-id="element-553">
            <img src="https://cdn.magicpatterns.com/uploads/rLCDkCKprcJW1NkEV2aCi1/Vitality_Logo_Circle.png" alt="Vitality Logo" className="w-10 h-10" data-id="element-554" />
            <div data-id="element-555">
              <p className="text-white font-medium" data-id="element-556">Vitality Venture Fund</p>
              <p className="text-gray-400 text-sm" data-id="element-557">
                Investing in nature-inspired innovation
              </p>
            </div>
          </div>

          <Link to="/insights" className="px-6 py-2 rounded-lg border border-white/20 text-white hover:bg-white/5 transition-colors" data-id="element-558">
            More Insights
          </Link>
        </motion.div>
      </div>
    </div>;
}